import { Tag, TagSize } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import usePopover from "components/hooks/usePopover";
import { Menu, MenuItem } from "@mui/material";
import { gql } from "__generated__/gql";
import { useMutation } from "@apollo/client";
import { Language } from "utils";
import { useState } from "react";
import ProductReviewDialog from "view/products/components/form/ProductReviewDialog";
import { useConfirm } from "components/hooks/useConfirm";
import { Product } from "types/index";

export const ProductStatusTag = ({ product, onChange }: { product: Pick<Product, "id" | "isActive">, onChange: () => void }) => {

	const { popoverProps, onPopoverOpen, onPopoverClose } = usePopover({ trigger: "click" });

	const confirm = useConfirm();

	const [ setActive ] = useMutation(PRODUCT_ACTIVE_MUTATION);

	const toggleIsActive = () => {
		setActive({ variables: { id: product.id, active: !product.isActive } }).then(() => onChange());
	};

	return (
		<div>
			<span style={{ cursor: "pointer" }} onClick={onPopoverOpen}>
				{product.isActive ? (
					<Tag color="#52c41a" text={intl.formatMessage({ id: "PRODUCTS_ACTIVE" })} size={TagSize.SMALL} />
				) : (
					<Tag color="#f5222d" text={intl.formatMessage({ id: "PRODUCTS_INACTIVE" })} size={TagSize.SMALL} />
				)}
			</span>

			<Menu {...popoverProps} onClose={onPopoverClose}>
				<MenuItem
					onClick={() => {
						onPopoverClose();

						confirm({
							type: "WARNING",
							title: intl.formatMessage({ id: "PRODUCT_DEACTIVATE" }),
							confirmText: intl.formatMessage({ id: "CONTINUE" }),
						}).then(() => toggleIsActive());
					}}>
					{product.isActive ? Language("INACTIVATE") : Language("ACTIVATE")}
				</MenuItem>
			</Menu>
		</div>
	);

};

export const ProductValidationStatusTag = ({ product, onChange }: { product: Pick<Product, "id" | "isValidated">, onChange: () => void }) => {

	const { popoverProps, onPopoverOpen, onPopoverClose } = usePopover({ trigger: "click" });

	const [ setValidated ] = useMutation(PRODUCT_VALIDATED_MUTATION);

	const toggleIsValid = () => {
		onPopoverClose();
		setValidated({ variables: { id: product.id, validated: !product.isValidated } }).then(() => onChange());
	};

	return (
		<div>
			<span style={{ cursor: "pointer" }} onClick={onPopoverOpen}>
				{product.isValidated ? (
					<Tag color="#52c41a" text={intl.formatMessage({ id: "PRODUCTS_VALIDATED" })} size={TagSize.SMALL} />
				) : (
					<Tag color="#f5222d" text={intl.formatMessage({ id: "PRODUCTS_NOT_VALIDATED" })} size={TagSize.SMALL} />
				)}
			</span>

			<Menu {...popoverProps} onClose={onPopoverClose}>
				<MenuItem onClick={toggleIsValid}>
					{product.isValidated ? Language("INVALIDATE") : Language("VALIDATE")}
				</MenuItem>
			</Menu>
		</div>
	);
};

export const ProductReviewStatusTag = ({ product }: { product: Pick<Product, "id" | "adnAttributes"> }) => {

	const { popoverProps, onPopoverOpen, onPopoverClose } = usePopover({ trigger: "click" });

	const [ adnReviewDialogOpen, setAdnReviewDialogOpen ] = useState(false);

	return (
		<div>
			<span className="product-status-tag-wrapper" onClick={onPopoverOpen}>
				<Tag color="#D18427" text={intl.formatMessage({ id: "PRODUCTS_NEEDS_MANUAL_REVIEW" })} size={TagSize.SMALL} />
			</span>

			<Menu {...popoverProps} onClose={onPopoverClose}>
				<MenuItem
					onClick={() => {
						onPopoverClose();
						setAdnReviewDialogOpen(true);
					}}>
					{intl.formatMessage({ id: "PRODUCTS_MANUAL_REVIEW" })}
				</MenuItem>
			</Menu>

			<ProductReviewDialog
				refetch={() => {}}
				product={product}
				open={adnReviewDialogOpen}
				onClose={() => setAdnReviewDialogOpen(false)} />
		</div>
	);
};

const PRODUCT_ACTIVE_MUTATION = gql(`#graphql
	mutation productSetActive($id: Int!, $active: Boolean!) {
		productSetActive(id: $id, active: $active) {
			id
		}
	}
`);

const PRODUCT_VALIDATED_MUTATION = gql(`#graphql
	mutation productSetValidated($id: Int!, $validated: Boolean!) {
		productSetValidated(id: $id, validated: $validated) {
			id
		}
	}
`);
